/**
 * The Delivery Note editor — create and edit the DRAFT only.
 *
 * WHY THERE IS NO PRODUCT PICKER HERE
 * ----------------------------------
 * Every other editor in this app starts from a product catalogue. A delivery note
 * cannot. It is a DISPATCH: it may only move quantity that a sales order already
 * promised, and the fulfilment engine derives what is still deliverable from the
 * link rows rather than from a number typed here. Offering a free product list
 * would offer to dispatch goods nobody ordered, and the service would reject it
 * anyway — so the screen would teach the user a rule it cannot explain.
 *
 * The consequence is that this editor is smaller than the invoice editor on
 * purpose: a date, the still-deliverable source lines, and their quantities.
 * Everything else — party, address, rates, tax — is SNAPSHOTTED from the order,
 * because a dispatch describes the transaction that promised the goods, not a new
 * commercial decision.
 *
 * And the thing it must never do is move stock. Nothing on this screen writes a
 * `StockTransaction`; issuing is the only event that does, and it lives on the
 * document screen.
 */
import { useMemo, useState } from 'react';
import { getDb } from '@data/db/database';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { convertSalesOrderToDeliveryNoteDraft } from '@data/services/deliveryNoteService';
import { DocumentScreen } from '@features/documents/DocumentScreen';
import { toast } from '@ui/components/Toaster';
import {
  useDeleteDeliveryNote,
  useDeliveryNote,
  useDeliverySource,
  useUpdateDeliveryNote,
} from './useDeliveryNotes';

const inputClass =
  'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100';

const today = () => new Date().toISOString().slice(0, 10);

/** One dispatchable source line, as this screen needs to draw it. */
interface DispatchLineState {
  /** The SOURCE line this dispatch consumes. Not a screen-local counter id. */
  readonly sourceLineId: string;
  readonly quantity: string;
  /** What the source line may still give THIS draft, in milli-units. */
  readonly available: number;
  readonly unit: string;
  readonly description: string;
  readonly tracked: boolean;
  readonly hasStockLocation: boolean;
}

/** The subset of a source line this screen reads. Structurally, not nominally. */
interface DispatchSourceLine {
  readonly id: string;
  readonly deliverableQtyMilli: number;
  readonly description: string;
  readonly unit: string;
  readonly isStockTracked: boolean;
  readonly stockLocationId?: string;
  readonly defaultStockLocationId?: string;
}
export function DeliveryNoteEditorScreen({
  documentId = null,
  salesOrderId = null,
}: {
  readonly documentId?: string | null;
  readonly salesOrderId?: string | null;
}) {
  const editing = documentId !== null;
  const existing = useDeliveryNote(documentId);
  const orderId = salesOrderId ?? existing.data?.stored.salesOrderId ?? null;
  const source = useDeliverySource(orderId, editing ? documentId : undefined);
  const update = useUpdateDeliveryNote();
  const remove = useDeleteDeliveryNote();
  const [docDate, setDocDate] = useState(today);
  const [lines, setLines] = useState<readonly DispatchLineState[]>([]);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * A source line as an editable row.
   *
   * `ownSavedMilli` is the quantity this draft already holds, and it is ADDED back
   * to the remaining allowance. The service read this draft's OWN link rows out of
   * the availability calculation — that is exactly what `excludeDeliveryNoteId` is
   * for — so without the addition, reopening a draft would show its own quantity
   * as unavailable and refuse to save it back unchanged.
   *
   * `prefillMilli` is what lands in the INPUT, and the two are different on
   * purpose. An edit restores exactly what was saved; a NEW dispatch prefills
   * everything still deliverable, because the common case is "dispatch the whole
   * order" and a blank quantity would make the user retype a number the screen
   * already knows.
   */
  function toDispatchState(
    line: DispatchSourceLine,
    ownSavedMilli: number,
    prefillMilli: number,
  ): DispatchLineState {
    return {
      sourceLineId: line.id,
      // The saved quantity, restored exactly. Not re-derived from the order: this
      // draft's own quantities ARE the edit, and recomputing them from a source
      // that has not changed would quietly discard the user's change.
      quantity: String(prefillMilli / 1000),
      available: Number(line.deliverableQtyMilli) + ownSavedMilli,
      unit: line.unit,
      description: line.description,
      tracked: line.isStockTracked,
      hasStockLocation: Boolean(line.stockLocationId ?? line.defaultStockLocationId),
    };
  }

/**
 * SEEDING THE FORM, DURING RENDER RATHER THAN IN AN EFFECT
 * ------------------------------------------------------
 * Both of these run while rendering, guarded by `loadedId`, and that is the
 * pattern `SalesOrderEditorScreen` already uses. An effect would be the obvious
 * spelling and it is worse here for two reasons:
 *
 *  - It re-runs on every refetch. React Query refetches constantly, so an effect
 *    would reset whatever the user had just typed back to the saved values —
 *    which reads as the editor ignoring the keyboard.
 *  - `setState` inside an effect body triggers a cascading second render. Seeding
 *    during render lets React discard the first pass and keep the second, so the
 *    user sees one settled form instead of a blank table that fills in.
 *
 * The guard is what makes it correct: it seeds exactly once per document, so
 * subsequent renders — including refetches — never touch the user's edits.
 */
  // An EDIT seeds from the stored document.
  //
  // `loaded` is the whole read result — the document row, its lines and its
  // source-link map — so the header date is `loaded.stored.docDate`, not
  // `loaded.docDate`. Naming it `stored` would read naturally and be wrong.
  if (editing && documentId && existing.data && loadedId !== documentId) {
    const loaded = existing.data;
    // The Map is built from a `map`/`filter` chain, so its element type widens to an
    // array union; the tuple annotation is what keeps it a `Map<string, number>`.
    const savedQtyBySourceLineId = new Map<string, number>(
      loaded.lines
        .map(
          (line): readonly [string, number] => [
            loaded.sourceLineByDeliveryLineId.get(line.id) ?? '',
            line.quantityMilli,
          ],
        )
        .filter(([sourceLineId]) => sourceLineId !== ''),
    );
    const sourceLines: readonly DispatchSourceLine[] = source.data?.lines ?? [];
    // Fall back to the stored lines when the source read has not landed yet, so a
    // reopened draft shows its own lines instead of an empty table.
    const base: readonly DispatchSourceLine[] = sourceLines.length
      ? sourceLines
      : loaded.lines.map(
          (line): DispatchSourceLine => ({
            id: loaded.sourceLineByDeliveryLineId.get(line.id) ?? '',
            deliverableQtyMilli: line.quantityMilli,
            description: line.description,
            unit: line.unit,
            isStockTracked: false,
          }),
        );
    setLines(
      base.map((line) => {
        const ownSaved = savedQtyBySourceLineId.get(line.id) ?? 0;
        // An edit restores the saved quantity; a draft whose source read has not
        // landed yet prefills from its own stored line, so the table is never blank.
        return toDispatchState(line, ownSaved, ownSaved || Number(line.deliverableQtyMilli));
      }),
    );
    setDocDate(loaded.stored.docDate);
    setLoadedId(documentId);
  }

  // A NEW dispatch from an order: offer everything still deliverable, fully
  // prefilled, because the common case is "dispatch the whole order".
  if (!editing && source.data && loadedId === null) {
    setLines(
      source.data.lines
        .filter((line) => Number(line.deliverableQtyMilli) > 0)
        .map((line) => toDispatchState(line, 0, Number(line.deliverableQtyMilli))),
    );
    setLoadedId('new');
  }

  const chosen = useMemo(() => lines.filter((line) => Number(line.quantity) > 0), [lines]);
  const totalQtyMilli = useMemo(
    () => chosen.reduce((sum, line) => sum + Math.round((Number(line.quantity) || 0) * 1000), 0),
    [chosen],
  );

  /**
   * A tracked line with nowhere to post the movement against.
   *
   * Surfaced here rather than only as a service refusal: the service rejects the
   * write regardless, but a message naming the item is one the user can act on
   * without losing their typed quantities.
   */
  const missingLocation = chosen.some((line) => line.tracked && !line.hasStockLocation);
  /** A line asking for more than the source has left. Also refused on save. */
  const overDispatched = chosen.some(
    (line) => Math.round((Number(line.quantity) || 0) * 1000) > line.available,
  );

  async function save() {
    if (!docDate || chosen.length === 0) {
      toast({ title: 'Choose at least one quantity to dispatch', tone: 'danger' });
      return;
    }
    const payload = {
      docDate,
      lines: chosen.map((line) => ({
        sourceLineId: line.sourceLineId,
        quantityMilli: Math.round(Number(line.quantity) * 1000),
      })),
    };
    setBusy(true);
    try {
      const db = getDb();
      const deviceId = await currentDeviceId(db);
      if (editing && documentId) {
        await update.mutateAsync({ id: documentId, input: { ...payload, deviceId } });
        toast({
          title: `Delivery note ${existing.data?.stored.docNumber} saved as draft`,
          tone: 'success',
        });
        return;
      }
      const created = await convertSalesOrderToDeliveryNoteDraft(db as never, {
        salesOrderId: orderId as string,
        docDate,
        deviceId,
      });
      toast({ title: `Delivery note ${created.docNumber} created as draft`, tone: 'success' });
      window.location.hash = `#/sales/deliveries/${created.id}/edit`;
    } catch (error) {
      toast({
        title: 'Could not save delivery note',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  async function deleteDraft() {
    if (!documentId || !window.confirm('Delete this delivery note draft?')) return;
    try {
      await remove.mutateAsync(documentId);
      toast({ title: 'Delivery note draft deleted', tone: 'success' });
      window.location.hash = '#/sales/deliveries';
    } catch (error) {
      toast({
        title: 'Could not delete delivery note',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    }
  }

  if (editing && existing.isLoading) {
    return <p className="p-4 text-sm text-slate-400">Loading delivery note…</p>;
  }
  // An ISSUED delivery note is immutable, and this screen has no business pretending
  // otherwise. The document screen is the correct destination — it is where the
  // printed copy, the status and the cancellation live.
  if (editing && existing.data && existing.data.stored.status !== 'DRAFT') {
    return <DocumentScreen documentId={documentId as string} />;
  }
  if (source.isLoading) return <p className="p-4 text-sm text-slate-400">Loading sales order…</p>;
  if (source.isError) {
    return (
      <p role="alert" className="p-4 text-sm text-red-300">
        {source.error instanceof Error
          ? source.error.message
          : 'This sales order cannot be dispatched.'}
      </p>
    );
  }

  const order = source.data?.order;

  return (
    <div className="flex flex-col gap-4 p-4 text-slate-100">
      <header>
        <h1 className="text-lg font-semibold">
          {editing ? 'Edit delivery note draft' : 'New delivery note'}
        </h1>
        <p className="text-xs text-slate-400">
          A delivery note records goods physically leaving the shop. Saving this draft moves no
          stock; issuing it does.
        </p>
      </header>

      {order && (
        <p data-testid="delivery-note-order" className="text-sm">
          Dispatch against {order.docNumber} · {order.partySnapshot.name}
        </p>
      )}
      {editing && existing.data && (
        <p data-testid="delivery-note-number" className="text-sm">
          {existing.data.stored.docNumber} · converted from sales order
        </p>
      )}

      <section className="flex flex-col gap-3 rounded border border-slate-800 p-4">
        <label className="block text-xs text-slate-400">
          Dispatch date
          <input
            data-testid="delivery-note-date"
            type="date"
            className={`${inputClass} mt-1`}
            value={docDate}
            onChange={(event) => setDocDate(event.target.value)}
          />
        </label>

        {lines.length === 0 ? (
          <p className="text-sm text-slate-400">
            Every quantity on this sales order has already been dispatched.
          </p>
        ) : (
          <table className="w-full text-sm">
            <caption className="sr-only">Quantities to dispatch</caption>
            <thead>
              <tr className="text-left text-xs uppercase text-slate-400">
                <th scope="col" className="p-2">
                  Item
                </th>
                <th scope="col" className="p-2">
                  Available
                </th>
                <th scope="col" className="p-2">
                  Dispatch qty
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.sourceLineId} data-testid="delivery-note-line">
                  <td className="p-2">
                    {line.description}
                    <span className="ml-1 text-xs text-slate-500">{line.unit}</span>
                  </td>
                  <td className="p-2 text-slate-400">{line.available / 1000}</td>
                  <td className="p-2">
                    <input
                      data-testid="delivery-note-quantity"
                      aria-label={`Dispatch quantity for ${line.description}`}
                      className={`${inputClass} w-24`}
                      inputMode="decimal"
                      max={line.available / 1000}
                      value={line.quantity}
                      onChange={(event) =>
                        setLines((old) =>
                          old.map((item) =>
                            item.sourceLineId === line.sourceLineId
                              ? { ...item, quantity: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {missingLocation && (
          <p role="alert" className="text-xs text-amber-300">
            A tracked item has no stock location. Set a default stock location in Settings before
            dispatching it.
          </p>
        )}
        {overDispatched && (
          <p role="alert" className="text-xs text-amber-300">
            A quantity exceeds what this sales order has left to dispatch.
          </p>
        )}
      </section>

      <aside className="h-fit rounded border border-slate-800 p-4">
        <h2 className="mb-3 text-xs uppercase text-slate-400">Dispatch summary</h2>
        <div className="flex justify-between border-t border-slate-700 py-2">
          <span>Total quantity</span>
          <span data-testid="delivery-note-total-qty">{totalQtyMilli / 1000}</span>
        </div>
        <button
          type="button"
          data-testid="delivery-note-save"
          disabled={busy || update.isPending || chosen.length === 0}
          onClick={() => void save()}
          className="mt-3 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium disabled:opacity-40"
        >
          {editing ? 'Save changes' : 'Create delivery note draft'}
        </button>
        {editing && documentId && (
          <>
            <a
              className="mt-2 block rounded border border-slate-700 px-3 py-2 text-center text-sm"
              href={`#/sales/deliveries/${documentId}`}
            >
              Preview without saving
            </a>
            <button
              type="button"
              data-testid="delete-delivery-note-draft"
              className="mt-2 w-full rounded border border-red-900 px-3 py-2 text-sm text-red-300"
              disabled={remove.isPending}
              onClick={() => void deleteDraft()}
            >
              Delete draft
            </button>
          </>
        )}
        <a className="mt-2 block text-center text-sm text-slate-400" href="#/sales/deliveries">
          Back to delivery notes
        </a>
      </aside>
    </div>
  );
}